import { signOut } from 'firebase/auth';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { auth } from '../firebase';
import { useAuth } from '../components_auth/AuthContext';
import {
  MAX_CLIENT_AUTH_AGE_MS,
  clearExternalAuthId,
  getBridgeConfig,
  isSafeRedirect,
  isValidExternalAuthId,
  readStoredExternalAuthId,
  storeExternalAuthId,
} from '../components_auth/oauthConnect';
import { Button } from '../components_catalyst/button';
import { Heading } from '../components_catalyst/heading';
import { Strong, Text, TextLink } from '../components_catalyst/text';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { loadCurrentUserProfile } from '../store/map3DataSlice';
import type { AppDispatch, RootState } from '../store/store';
import { MCP_ACCESS_REQUEST_EMAIL } from './mcpAccess';

type Problem =
  | 'framed'
  | 'missing_id'
  | 'not_configured'
  | 'reauth'
  | 'access_denied'
  | 'expired'
  | 'rate_limited'
  | 'unavailable'
  | 'unsafe_redirect';

const PROBLEM_TEXT: Record<Exclude<Problem, 'access_denied' | 'reauth'>, string> = {
  framed: 'For your security, this page cannot be shown inside another page. Open Milestone Maps in its own browser tab to connect an AI app.',
  missing_id:
    'This page is opened by your AI app when you connect it to Milestone Maps. Go back to the app and start connecting again.',
  not_configured: 'Connecting AI apps is not set up for this environment.',
  expired: 'This connection request expired or was already used. Go back to your AI app and start again.',
  rate_limited: 'Too many attempts. Wait a minute and try again.',
  unavailable: 'We could not complete the connection right now. Try again in a moment.',
  unsafe_redirect: 'The connection could not be completed safely. Go back to your AI app and start again.',
};

/** True when this page is embedded in another page (clickjacking, SPEC.Auth B8). Defence in depth next to the CSP header. */
function isFramed(): boolean {
  try {
    return window.self !== window.top;
  } catch {
    return true; // a cross-origin top that throws is also a frame
  }
}

const ERROR_CLASSES =
  'rounded-md border border-red-300 bg-red-50 p-4 text-sm text-red-900 focus:outline-none dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-200';

/**
 * Login bridge page (/oauth/connect), the authorization server's Login URI (SPEC.Auth section 2.3).
 * Lives OUTSIDE UserProfileLoader. Keeps the AS's single-use `external_auth_id` in sessionStorage
 * through /sign-in, shows who is connecting, requires an explicit Continue (no auto-submit, B4),
 * POSTs a fresh ID token to the MCP server and follows the returned redirect only when it is on the
 * configured issuer origin (B6, INV-18).
 */
export function OAuthConnectPage() {
  useDocumentTitle('Connect an AI app');
  const { user } = useAuth();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const [searchParams] = useSearchParams();
  const userProfile = useSelector((state: RootState) => state.map3Data.userProfile);
  const [problem, setProblem] = useState<Problem | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [framed] = useState(isFramed);
  const alertRef = useRef<HTMLDivElement>(null);
  const config = getBridgeConfig();

  const queryId = searchParams.get('external_auth_id');
  const [externalAuthId] = useState<string | null>(() => {
    // A present-but-malformed query value is tampering: do not fall back to an older stored id.
    if (queryId !== null) return isValidExternalAuthId(queryId) ? queryId : null;
    return readStoredExternalAuthId();
  });

  // Persist the id, then drop it from the URL so it does not linger in history or referrers.
  useEffect(() => {
    if (framed) return; // never store or act on an id from inside a frame
    if (queryId !== null) {
      if (isValidExternalAuthId(queryId)) storeExternalAuthId(queryId);
      navigate('/oauth/connect', { replace: true });
    }
  }, [framed, queryId, navigate]);

  // Not signed in: go through /sign-in; it returns here because the id is in sessionStorage.
  useEffect(() => {
    if (!framed && !user && externalAuthId) navigate('/sign-in', { replace: true });
  }, [framed, user, externalAuthId, navigate]);

  useEffect(() => {
    if (user?.uid && userProfile?.uid !== user.uid) void dispatch(loadCurrentUserProfile());
  }, [dispatch, user?.uid, userProfile?.uid]);

  // WCAG 2.4.3: the focused Continue button is disabled and may be replaced, so move focus to the outcome.
  useEffect(() => {
    if (problem) alertRef.current?.focus();
  }, [problem]);

  const signInAgain = useCallback(async () => {
    try {
      await signOut(auth);
    } finally {
      navigate('/sign-in'); // the external_auth_id stays in sessionStorage
    }
  }, [navigate]);

  const cancel = () => {
    clearExternalAuthId();
    navigate('/');
  };

  const onContinue = async () => {
    if (!user || !externalAuthId || !config) return;
    setProblem(null);
    setSubmitting(true);
    try {
      const tokenResult = await user.getIdTokenResult(true);
      const authTime = Date.parse(tokenResult.authTime);
      if (!Number.isFinite(authTime) || Date.now() - authTime > MAX_CLIENT_AUTH_AGE_MS) {
        setProblem('reauth');
        return;
      }
      const res = await fetch(config.completeUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'omit',
        cache: 'no-store',
        redirect: 'error',
        body: JSON.stringify({ idToken: tokenResult.token, externalAuthId }),
      });
      const body = (await res.json().catch(() => ({}))) as { redirectUrl?: unknown; error?: string };
      if (res.ok) {
        if (!isSafeRedirect(body.redirectUrl, config.issuerOrigin)) return setProblem('unsafe_redirect');
        clearExternalAuthId();
        window.location.assign(body.redirectUrl);
        return;
      }
      if (res.status === 401) return setProblem('reauth');
      if (res.status === 403) return setProblem('access_denied');
      if (res.status === 400) {
        clearExternalAuthId(); // single-use: a retry with the same id can never succeed
        return setProblem('expired');
      }
      if (res.status === 429) return setProblem('rate_limited');
      setProblem('unavailable');
    } catch {
      setProblem('unavailable');
    } finally {
      setSubmitting(false);
    }
  };

  const mailto = `mailto:${MCP_ACCESS_REQUEST_EMAIL}?subject=${encodeURIComponent('MCP ACCESS REQUEST')}&body=${encodeURIComponent(
    `Please enable AI agent (MCP) access for ${user?.email ?? 'my account'}.`,
  )}`;

  const fatal: Problem | null = framed ? 'framed' : !externalAuthId ? 'missing_id' : !config ? 'not_configured' : null;
  const shownName = userProfile?.uid === user?.uid ? userProfile?.userName : undefined;

  return (
    <div className="mx-auto max-w-lg px-6 py-12">
      <Heading level={1}>Connect an AI app</Heading>

      {fatal ? (
        <div role="alert" className="mt-6 space-y-4" data-testid="oauth-connect-error">
          <Text>{PROBLEM_TEXT[fatal as keyof typeof PROBLEM_TEXT]}</Text>
          {fatal !== 'framed' && (
            <Text>
              <TextLink href="/">Back to Milestone Maps</TextLink>
            </Text>
          )}
        </div>
      ) : !user ? (
        <div role="status" aria-live="polite" className="mt-6">
          <Text>Taking you to sign in...</Text>
        </div>
      ) : (
        <div className="mt-6 space-y-6">
          <section aria-labelledby="oauth-connect-who">
            <Text id="oauth-connect-who">An AI app is asking to connect to Milestone Maps as:</Text>
            <Text className="mt-2" data-testid="oauth-connect-identity">
              {shownName ? (
                <>
                  <Strong>{shownName}</Strong> ({user.email})
                </>
              ) : (
                <Strong>{user.email}</Strong>
              )}
            </Text>
            <Text className="mt-2">Only continue if you just started connecting an app yourself.</Text>
            <Text className="mt-2">Not you? Choose Switch account.</Text>
          </section>

          {/* Polite announcement of progress; the button label alone is not reliably announced. */}
          <span role="status" aria-live="polite" className="sr-only" data-testid="oauth-connect-status">
            {submitting ? 'Connecting...' : ''}
          </span>

          {problem && (
            <div ref={alertRef} tabIndex={-1} role="alert" className={ERROR_CLASSES} data-testid="oauth-connect-error">
              {problem === 'access_denied' ? (
                <>
                  This account does not have AI agent access yet. <TextLink href={mailto}>Request access</TextLink> or
                  switch to another account.
                </>
              ) : problem === 'reauth' ? (
                <>For your security, sign in again to connect an AI app.</>
              ) : (
                <>
                  {PROBLEM_TEXT[problem]}
                  {problem === 'expired' && (
                    <>
                      {' '}
                      <TextLink href="/">Back to Milestone Maps</TextLink>
                    </>
                  )}
                </>
              )}
            </div>
          )}

          <div className="flex flex-wrap items-center gap-3">
            {problem === 'reauth' ? (
              // Signing in again IS switching account here; one control, not two that do the same thing.
              <Button color="blue" onClick={signInAgain}>
                Sign in again
              </Button>
            ) : (
              <>
                {problem !== 'expired' && (
                  <Button color="blue" onClick={onContinue} disabled={submitting}>
                    {submitting ? 'Connecting...' : 'Continue'}
                  </Button>
                )}
                <Button plain onClick={signInAgain} disabled={submitting}>
                  Switch account
                </Button>
              </>
            )}
            <Button outline onClick={cancel} disabled={submitting}>
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
